import React from 'react';
import { useDropzone, FileError } from 'react-dropzone';
import { Image as ImageIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { CoverPhoto } from '@epoch-ui/components/CoverPhoto';

const CoverPhotoInputContext = React.createContext<{
  disabled?: boolean;
  open: () => void;
}>({
  open: () => {
    /** noop */
  }
});

export interface CoverPhotoInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  photoUrl?: string;
  loading?: boolean;
  error?: boolean;
  noClick?: boolean;
  noKeyboard?: boolean;
  onFileAccepted: (file: File) => void;
  onFileRejected: (file: File, errors: FileError[]) => void;
}

/**
 * CoverPhotoInput is a standalone component for handling cover photo uploads.
 * It provides a customizable dropzone for image files, supporting drag-and-drop
 * and click-to-upload functionality. This component is not tied to any specific
 * form or data submission logic and can be used independently.
 *
 * It is a controlled component, which means that the parent component should
 * manage the state of the input.
 *
 * Use <CoverPhotoField /> in `@epoch-ui/components/Form/fields` in React Hooks Form context.
 *
 * In addition to the regular props for HTMLInputElement, it also accepts:
 * @param {string} [props.photoUrl] - URL of uploaded image to display as a preview
 * @param {boolean} [props.loading] - Whether the component is in a loading state
 * @param {boolean} [props.error] - Whether to display an error state
 * @param {boolean} [props.noClick] (default: false) - Disable click to open file dialog.
 * This is useful when handling open file dialog programmatically.
 * @param {boolean} [props.noKeyboard] (default: false) - Disable keyboard interaction.
 * This is useful when handling open file dialog programmatically.
 * @param {function} props.onFileAccepted - Callback function when a file is accepted.
 * It receives the accepted file as an argument.
 * @param {function} props.onFileRejected - Callback function when a file is rejected.
 * It receives the rejected file and errors as arguments.
 */
const CoverPhotoInput = React.forwardRef<HTMLDivElement, CoverPhotoInputProps>(
  (
    {
      className,
      children,
      photoUrl,
      loading,
      error,
      noClick,
      noKeyboard,
      onFileAccepted,
      onFileRejected,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg']
      },
      disabled: props.disabled || loading,
      maxFiles: 1,
      multiple: false,
      maxSize: 5242880, // 5mb
      onDropAccepted: ([file]) => {
        onFileAccepted(file);
      },
      onDropRejected: ([{ file, errors }]) => {
        onFileRejected(file, Array.from(errors));
      },
      noClick,
      noKeyboard
    });

    const handleOpen = React.useCallback(() => {
      if (inputRef.current) {
        inputRef.current.click();
      }
    }, []);

    return (
      <CoverPhotoInputContext.Provider value={{ open: handleOpen, disabled: props.disabled }}>
        <div
          {...getRootProps({
            disabled: props.disabled,
            ref
          })}
          className={cn(
            'tw-relative tw-flex tw-aspect-square tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[16px] tw-bg-background-grey',
            props.disabled && 'tw-opacity-50',
            isDragActive && 'tw-border-2 tw-border-solid tw-border-primary-blue',
            error &&
              !isDragActive &&
              'tw-border-2 tw-border-solid tw-border-destructive-foreground',
            className
          )}
        >
          <input
            {...getInputProps({
              ...props
            })}
            ref={inputRef}
          />
          {loading && <FontAwesomeIcon size="3x" icon={faSpinner} spin />}
          {/* Image Preview */}
          {photoUrl && !loading && (
            <CoverPhoto src={photoUrl} alt="Cover Photo" className="tw-rounded-[12px]" />
          )}
          {/* Image Placeholder */}
          {!photoUrl && !loading && <ImageIcon size={64} className="tw-text-medium-grey" />}

          {/* Action Button, if any */}
          {children}
        </div>
      </CoverPhotoInputContext.Provider>
    );
  }
);

CoverPhotoInput.displayName = 'CoverPhotoInput';

/**
 * CoverPhotoInputActions is a component that renders a container for actions related
 * to the cover photo input. It provides a flexible container for adding various
 * actions (e.g. Open file dialog, Remove photo, etc.) related to the cover photo input.
 */
const CoverPhotoInputActions: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => {
  return (
    <div
      {...props}
      className={cn(
        'tw-absolute tw-bottom-4 tw-left-0 tw-right-0 tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-end tw-gap-4 tw-p-4',
        className
      )}
    />
  );
};

CoverPhotoInputActions.displayName = 'CoverPhotoInputActions';

/**
 * CoverPhotoInputAction is a component that renders a button with a custom styling
 * for cover photo input actions.
 */
const CoverPhotoInputAction: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  ...props
}) => {
  return (
    <Button
      {...props}
      type="button"
      className={cn(
        'tw-aspect-square tw-h-10 tw-rounded-full tw-bg-white tw-p-2 tw-text-black tw-shadow-custom hover:tw-bg-background-grey',
        className
      )}
    />
  );
};

CoverPhotoInputAction.displayName = 'CoverPhotoInputAction';

/**
 * CoverPhotoInputOpenAction is a component that renders a button for opening the file dialog.
 */
const CoverPhotoInputOpenAction: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  ...props
}) => {
  const { open, disabled } = React.useContext(CoverPhotoInputContext);

  return (
    <CoverPhotoInputAction
      {...props}
      className={cn(className)}
      onClick={open}
      disabled={disabled}
    />
  );
};

CoverPhotoInputOpenAction.displayName = 'CoverPhotoInputOpenAction';

export {
  CoverPhotoInput,
  CoverPhotoInputActions,
  CoverPhotoInputAction,
  CoverPhotoInputOpenAction,
  type FileError
};
